<script lang="ts">
  import { _ } from "svelte-i18n";
  import Typography from "$lib/components//Typography/Typography.svelte";
  import Link from "$lib/components/Link/Link.svelte";
  import type { FliteImage as FI } from "$lib/gql/genCms/codegen";
  import {
    cardImageConfig,
    twoImageHeroConfig,
  } from "./EditorialCard.image-config";
  import {
    Picture,
    FliteImage,
    type ImageInputConfig,
  } from "@thisissoon/flitejs-frontend";
  import { linkTo } from "$lib/utils/url";
  // import TwoImageHeroCounter from "$lib/components/Counter/TwoImageHeroCounter.svelte";
  import inView from "$lib/utils/inView";

  export let title: string;
  export let description: string = "";
  export let image: FI;
  export let url: string;
  export let fontVariant = "button1";
  export let ctaText: string;
  export let twoImageHero = false;
  export let paddingLg = false;
  export let imgBorder = false;
  // export let animate: boolean;
  export let invert: boolean;
  export let colour: string;
  export let theme: any;

  export let imageConfig: ImageInputConfig = cardImageConfig;

  $: if (twoImageHero) {
    imageConfig = twoImageHeroConfig;
  }

  let shouldAnimate = false;

  let viewportWidth: number;
  let isMobile = false;
  let mdWidth = 768;

  $: isMobile = viewportWidth < mdWidth;
</script>

<style lang="scss">
  .editorial-card:hover .editorial-card--img {
    transform: scale(1.08);
    transform-origin: center;
  }

  :global(.editorial-card--img > div) {
    height: 100%;
  }

  :global(.editorial-card--img-wrapper .editorial-card--img .inner.coverImage) {
    overflow: visible;
  }
  .editorial-card {
    display: block;
    position: relative;

    &--anchor {
      position: static;
      text-decoration: none;

      /* make the anchor tag cover the entire editorial card area */
      &:before {
        content: "";
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 3;
      }
    }

    &--img {
      aspect-ratio: 4/5;
      width: calc(100% + 1px);

      &-wrapper {
        aspect-ratio: 4/5;
        display: block;
        position: relative;
        overflow: hidden;

        &__border {
          border-bottom: 0.5px solid $background;
        }
      }

      transition: 300ms ease-in-out;
    }

    &--info {
      position: relative;
      height: 100%;
      background-color: $surface;
      padding: 8px;

      &__invert {
        background-color: $inverse-surface;
      }

      &__description {
        padding-top: 8px;
      }

      &__padded-lg {
        padding: 16px;
      }

      &__two-image-hero {
        padding-top: 20px;
      }
    }
    &--link {
      padding-top: 8px;
      color: $on-background;
      transition: color 300ms ease-in-out;

      &:hover {
        color: $on-surface-30;
      }
    }
  }
  .counter {
    position: absolute;
    z-index: 1000;
  }
</style>

<svelte:window bind:innerWidth={viewportWidth} />

<div
  class="editorial-card"
  use:inView={{ threshold: 0.4 }}
  on:intersect={({ detail }) => {
    shouldAnimate = detail;
  }}
>
  {#if url}
    <a
      class="editorial-card--anchor"
      href={linkTo(url)}
      aria-label={`View ${title}`}
    >
      <div
        class={`editorial-card--img-wrapper ${
          imgBorder && "editorial-card--img-wrapper__border"
        }`}
      >
        <div class="editorial-card--img">
          {#if image && image.uri}
            <div>
              <!-- {#if animate}
                <div class="counter">
                  <TwoImageHeroCounter {invert} {shouldAnimate} />
                </div>
              {/if} -->
              <Picture
                imageInput={FliteImage.fromURL(image.uri)}
                imageInputConfig={imageConfig}
                altText={image.altText}
                coverImage
              />
            </div>
          {/if}
        </div>
      </div>
    </a>
  {:else}
    <div
      class={`editorial-card--img-wrapper ${
        imgBorder && "editorial-card--img-wrapper__border"
      }`}
    >
      <div class="editorial-card--img">
        {#if image && image.uri}
          <Picture
            imageInput={FliteImage.fromURL(image.uri)}
            imageInputConfig={imageConfig}
            altText={image.altText}
            coverImage
          />
        {/if}
      </div>
    </div>
  {/if}

  {#if title || description || (url && ctaText)}
    <div
      class={`editorial-card--info${invert ? "__invert" : ""} ${
        paddingLg && "editorial-card--info__padded-lg"
      }
          ${twoImageHero && "editorial-card--info__two-image-hero"}
      }`}
    >
      <div class="editorial-card--info__base">
        {#if title}
          <div class="editorial-card--info__title">
            <Typography
              role="heading"
              aria-level={3}
              component="span"
              theme={invert ? "inverse-on-surface" : "on-surface"}
              variant={twoImageHero
                ? isMobile
                  ? "h2"
                  : "h1"
                : fontVariant
                  ? fontVariant
                  : "button1"}>{title}</Typography
            >
          </div>
        {/if}
        {#if description}
          <div class="editorial-card--info__description">
            <Typography
              component="span"
              variant="body1"
              theme={invert ? "inverse-on-surface" : "on-surface"}
              >{description || ""}</Typography
            >
          </div>
        {/if}
      </div>
      <div>
        {#if url && ctaText}
          <div class="editorial-card--link">
            <Link
              text={ctaText}
              href={linkTo(url)}
              icon="east"
              iconLeft={false}
              underline={false}
              standard={false}
              iconTheme="on-background"
              processedUrl
              {invert}
              {colour}
              {twoImageHero}
              {theme}
            />
          </div>
        {/if}
      </div>
    </div>
  {/if}
</div>
